import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Headset, Info } from 'lucide-react'
import type { SupportPriority, SupportType } from '@/api/supportApi'
import { AttachmentTools, AttachmentTray, FileDropZone, usePendingFiles } from '@/components/support/SupportAttachments'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ReportedButFileFailed, useReportIssue, useSupportStatus, useSupportTickets } from '@/hooks/useSupport'
import { similarTickets } from '@/lib/support/supportFiles'
import { NOT_CONNECTED, PHI_REMINDER, PRIORITY_OPTIONS, TYPE_OPTIONS } from '@/lib/support/supportView'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useTenant } from '@/providers/TenantProvider'

/** A related record as Smart Support's reference takes it: letters, digits and : . _ / - only. */
const toReference = (s: string) =>
  s
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9:._/-]/g, '')
    .replace(/^[^A-Za-z0-9]+/, '')
    .slice(0, 150)

/**
 * Report an issue to Title21: what kind, a subject (with open tickets that
 * look the same), what happened with files, screenshots and links, how
 * urgent, and an optional related CGT record. The page, tenant, person and
 * browser go along. On success the ticket opens on the Support page.
 */
export function ReportIssueDialog({ open, onOpenChange, fromUrl }: { open: boolean; onOpenChange: (open: boolean) => void; fromUrl: string }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectedTenant } = useTenant()
  const status = useSupportStatus(open)
  const connected = status.data?.connected ?? false
  const tickets = useSupportTickets(open && connected)
  const report = useReportIssue()
  const pending = usePendingFiles()
  const [type, setType] = useState<SupportType>('defect')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<SupportPriority>('P3')
  const [record, setRecord] = useState('')

  const reason = status.data && !status.data.connected ? status.data.reason : null
  const similar = useMemo(() => (subject.trim().length >= 4 ? similarTickets(subject, tickets.data?.items ?? []) : []), [subject, tickets.data])
  const valid = subject.trim().length > 0 && description.trim().length > 0
  const page = pathOf(fromUrl)
  const fileError = report.error instanceof ReportedButFileFailed ? report.error : null

  const reset = () => {
    setSubject('')
    setDescription('')
    setType('defect')
    setPriority('P3')
    setRecord('')
    pending.clear()
    report.reset()
  }
  const close = () => {
    onOpenChange(false)
    reset()
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid || !connected) return
    const reference = toReference(record)
    report.mutate(
      {
        input: { type, subject: subject.trim(), description: description.trim(), priority, pageUrl: fromUrl, ...(reference ? { reference } : {}) },
        files: pending.files,
      },
      {
        onSuccess: (t) => {
          close()
          navigate(`/support/${t.number}`)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className='max-h-[92vh] max-w-2xl overflow-y-auto p-0'>
        <form onSubmit={submit} className='flex flex-col'>
          <DialogHeader className='border-b border-border px-6 py-4'>
            <DialogTitle className='flex items-center gap-2 text-base'>
              <span className='flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary'>
                <Headset className='h-4 w-4' aria-hidden />
              </span>
              Report an issue
            </DialogTitle>
            <DialogDescription>
              To Title21 Support{selectedTenant ? ` · ${selectedTenant.name}` : ''}. They answer on the Support page and in your notifications.
            </DialogDescription>
          </DialogHeader>

          <div className='flex flex-col gap-5 px-6 py-5'>
            {reason ? <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>{NOT_CONNECTED[reason]}</p> : null}

            <Field label='What kind of issue?'>
              <div className='flex flex-wrap gap-1.5' role='radiogroup' aria-label='Issue type'>
                {TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type='button'
                    role='radio'
                    aria-checked={type === o.value}
                    title={o.hint}
                    onClick={() => setType(o.value)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs transition-colors',
                      type === o.value ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label='Subject' htmlFor='support-subject'>
              <Input id='support-subject' autoFocus value={subject} maxLength={200} placeholder='A short summary, e.g. Label printer offline in the cleanroom' onChange={(e) => setSubject(e.target.value)} />
              {similar.length ? (
                <div className='mt-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-900'>
                  <p className='mb-1 flex items-center gap-1 font-medium'>
                    <Info className='h-3.5 w-3.5' aria-hidden /> Already open at {selectedTenant?.name ?? 'this tenant'} — follow one instead?
                  </p>
                  <ul className='flex flex-col gap-0.5'>
                    {similar.map((t) => (
                      <li key={t.number}>
                        <Link to={`/support/${t.number}`} onClick={close} className='hover:underline'>
                          <span className='font-mono'>{t.key}</span> · {t.subject}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </Field>

            <Field label='What happened?' htmlFor='support-description'>
              <FileDropZone pending={pending} className='rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring'>
                <textarea
                  id='support-description'
                  rows={6}
                  value={description}
                  maxLength={20000}
                  placeholder={'Steps, what you expected, what you saw.\nDrop or paste files and screenshots here.'}
                  onChange={(e) => setDescription(e.target.value)}
                  className='block w-full resize-y rounded-t-lg bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground'
                />
                <div className='flex flex-col gap-2 border-t border-border px-2 py-1.5'>
                  <AttachmentTray pending={pending} />
                  <AttachmentTools pending={pending} onLink={(url) => setDescription((d) => (d.trim() ? `${d.trimEnd()}\n${url}` : url))} />
                </div>
              </FileDropZone>
              <p className='mt-1.5 text-xs text-amber-700'>{PHI_REMINDER}</p>
            </Field>

            <div className='grid gap-5 sm:grid-cols-[1fr_1fr]'>
              <Field label='How urgent?'>
                <div className='inline-flex rounded-lg border border-border p-0.5' role='radiogroup' aria-label='Priority'>
                  {PRIORITY_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type='button'
                      role='radio'
                      aria-checked={priority === o.value}
                      title={o.hint}
                      onClick={() => setPriority(o.value)}
                      className={cn(
                        'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                        priority === o.value ? (o.value === 'P1' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground') : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className='mt-1 text-xs text-muted-foreground'>{PRIORITY_OPTIONS.find((o) => o.value === priority)?.hint}</p>
              </Field>
              <Field label='Related record (optional)' htmlFor='support-record'>
                <Input id='support-record' value={record} maxLength={150} placeholder='e.g. DIN W0000 26 123456' onChange={(e) => setRecord(e.target.value)} />
              </Field>
            </div>

            <p className='text-xs text-muted-foreground'>
              Sent with it: <span className='text-foreground'>{page}</span> · {selectedTenant?.name ?? 'this tenant'} · {user?.fullName || user?.email || 'you'} · {navigatorLabel()}
            </p>

            {fileError ? (
              <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                {fileError.message}{' '}
                <Link to={`/support/${fileError.number}`} onClick={close} className='font-medium underline'>
                  Open the ticket
                </Link>
              </p>
            ) : report.error ? (
              <p className='text-sm text-destructive'>{(report.error as Error).message}</p>
            ) : null}
          </div>

          <DialogFooter className='border-t border-border px-6 py-3'>
            <span className='mr-auto self-center text-xs text-muted-foreground'>
              {pending.files.length ? `${pending.files.length} file${pending.files.length === 1 ? '' : 's'} attached` : ''}
            </span>
            <Button type='button' variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button type='submit' disabled={!valid || !connected || report.isPending}>
              {report.isPending ? 'Sending…' : 'Send to Title21'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className='flex flex-col'>
      <Label htmlFor={htmlFor} className='mb-1.5 text-xs font-semibold text-foreground'>
        {label}
      </Label>
      {children}
    </div>
  )
}

function pathOf(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

function navigatorLabel(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser'
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} · ${os}` : browser
}
